import {
  Fragment,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { ShareableIncidentDataV1Response } from "@thalamos/common";
import type {
  FormContextData,
  OutcomeFormPlaceOfSafety,
  OutcomeFormSummary,
} from "@thalamos/aspire-common";

import { generateMenuOptions } from "./helpers.js";
import { resolveEmhaParticipant } from "./logic.js";
import { OutcomeFormPosSelectionModal } from "./OutcomeFormPosSelectionModal.js";
import { api, apiHooks } from "~/api.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Button,
  LoadingSpinner,
  MenuFlyout,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { PopupDialog } from "~/components/design-system/PopupDialog/PopupDialog.js";
import { PopupDialogTitle } from "~/components/design-system/PopupDialog/PopupDialogTitle.js";
import { FormCategoryRow } from "~/components/FormCategoryRow.js";
import { MultiPagePdf } from "~/components/MultiPagePdf.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";
import { logErrorToDatadog } from "~/tracing.js";

// --- Helpers ---

/** Check whether two OutcomeFormPlaceOfSafety values refer to the same POS. */
function posMatches(
  a: OutcomeFormPlaceOfSafety,
  b: OutcomeFormPlaceOfSafety,
): boolean {
  if (a.curatedPlaceOfSafetyId && b.curatedPlaceOfSafetyId) {
    return a.curatedPlaceOfSafetyId === b.curatedPlaceOfSafetyId;
  }
  // Fall back to manual field comparison
  return (
    !!a.manualPosName &&
    a.manualPosName === b.manualPosName &&
    a.manualPosAddress === b.manualPosAddress &&
    a.manualPosPostalCode === b.manualPosPostalCode
  );
}

// --- Sub-components ---

interface OutcomeFormCardProps {
  summary: OutcomeFormSummary;
  isOwner: boolean;
  ownerLabel: string;
  onContinue: () => void;
  onClaim: () => void;
  isSubmitting: boolean;
  patientId: string;
  subtitle: string;
  /** When true the current user is at a different POS – they may only view the PDF. */
  isAtDifferentPos: boolean;
  /** When true the incident is finalised – creation and completion are blocked. */
  isFinalised: boolean;
}

/**
 * Card for an in-progress outcome form linked to a specific POS.
 */
function OutcomeFormCard({
  summary,
  isOwner,
  ownerLabel,
  onContinue,
  onClaim,
  isSubmitting,
  subtitle,
  isAtDifferentPos,
  isFinalised,
}: OutcomeFormCardProps) {
  const { t } = useTranslation();
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimPosConfirmed, setClaimPosConfirmed] = useState(false);
  const [showDraftPdfDialog, setShowDraftPdfDialog] = useState(false);
  const [draftPdfData, setDraftPdfData] = useState<string | null>(null);

  const draftId = summary.formDraftId;

  const handleViewDraftPdf = async () => {
    if (!draftId) return;
    setShowDraftPdfDialog(true);
    setDraftPdfData(null);
    try {
      const result = await api.drafts.getPdf(draftId, false);
      if (result.status === 200 && result.data) {
        setDraftPdfData(result.data);
      } else {
        renderErrorToast({
          message: t("pages.policeIncidentFormPage.failedPdfDownload"),
        });
        setShowDraftPdfDialog(false);
      }
    } catch {
      renderErrorToast({
        message: t("pages.policeIncidentFormPage.failedPdfDownload"),
      });
      setShowDraftPdfDialog(false);
    }
  };

  return (
    <>
      <Box
        data-testid={`outcome-form-card-${summary.formId}`}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            p: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "common.blueGray",
                fontWeight: 600,
              }}
            >
              {t("pages.policeIncidentFormPage.formTitles.sectionOutcome")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {subtitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {summary.posDisplayName}
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            >
              {isOwner ? (
                <Chip
                  label={t(
                    "pages.policeIncidentFormPage.outcomeForm.yourDraft",
                  )}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label={t(
                    "pages.policeIncidentFormPage.outcomeForm.lastWorkedOnBy",
                    { name: ownerLabel },
                  )}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            {isAtDifferentPos ? (
              <Button
                label={t("pages.policeIncidentFormPage.outcomeForm.viewPdf")}
                variant="outlined"
                onClick={() => void handleViewDraftPdf()}
                disabled={!draftId}
              />
            ) : (
              <Button
                label={t("pages.policeIncidentFormPage.continue")}
                onClick={
                  isOwner
                    ? onContinue
                    : () => {
                        setShowClaimDialog(true);
                      }
                }
                disabled={isSubmitting || isFinalised}
              />
            )}
          </Box>
        </Box>
      </Box>
      {/* Claim confirmation dialog — shown when a non-owner clicks Continue */}
      <PopupDialog
        open={showClaimDialog}
        onClose={() => {
          setShowClaimDialog(false);
          setClaimPosConfirmed(false);
        }}
      >
        <DialogContent>
          <DialogContentText>
            {t("pages.policeIncidentFormPage.outcomeForm.claimConfirmMessage", {
              name: ownerLabel,
              posName: summary.posDisplayName,
            })}
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={claimPosConfirmed}
                onChange={(e) => {
                  setClaimPosConfirmed(e.target.checked);
                }}
                data-testid="claim-pos-confirm-checkbox"
              />
            }
            label={t(
              "pages.policeIncidentFormPage.outcomeForm.claimPosConfirmCheckbox",
              { posName: summary.posDisplayName },
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            label={t("buttonLabels.goBack")}
            onClick={() => {
              setShowClaimDialog(false);
              setClaimPosConfirmed(false);
            }}
          />
          <Button
            label={t("pages.policeIncidentFormPage.continue")}
            disabled={isSubmitting || !claimPosConfirmed}
            onClick={() => {
              setShowClaimDialog(false);
              setClaimPosConfirmed(false);
              onClaim();
            }}
          />
        </DialogActions>
      </PopupDialog>
      {/* Draft PDF viewer dialog — shown for users at a different POS */}
      <PopupDialog
        open={showDraftPdfDialog}
        onClose={() => {
          setShowDraftPdfDialog(false);
        }}
        hasPadding={false}
      >
        <Box sx={{ my: 2, mx: 7.5 }}>
          <PopupDialogTitle
            titleText={t(
              "pages.policeIncidentFormPage.formTitles.sectionOutcome",
            )}
            subtitleText={summary.posDisplayName}
            closeDialog={() => {
              setShowDraftPdfDialog(false);
            }}
            removeMarginBottom
          />
        </Box>
        <Box
          sx={{
            mx: 2,
            minHeight: "60vh",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {draftPdfData ? (
            <MultiPagePdf data={draftPdfData} />
          ) : (
            <LoadingSpinner />
          )}
        </Box>
        <Box
          sx={{
            mb: 3,
            width: "100%",
            position: "sticky",
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 99,
            background: "white",
            p: 1,
          }}
        >
          <Button
            variant="outlined"
            label={t("buttonLabels.close")}
            onClick={() => {
              setShowDraftPdfDialog(false);
            }}
          />
        </Box>
      </PopupDialog>
    </>
  );
}

interface OutcomeFormCompletedCardProps {
  summary: OutcomeFormSummary;
  subtitle: string;
  menuOptions: MenuOptionsType[];
}

/**
 * Card for a completed outcome form.
 */
function OutcomeFormCompletedCard({
  summary,
  subtitle,
  menuOptions,
}: OutcomeFormCompletedCardProps) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [actionsIsOpen, setActionsIsOpen] = useState(false);

  const isAutoClosedForm = summary.completionStatus === "auto-closed";

  return (
    <Box
      data-testid={`outcome-form-completed-${summary.formId}`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        backgroundColor: "#FCFCFC",
        borderRadius: (theme) => theme.spacing(0.75),
      }}
    >
      <Box
        sx={{
          borderLeft: (theme) =>
            `${theme.spacing(0.4)} solid ${theme.palette.primary.main}`,
          pl: 1.2,
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 400 }}>
          {t("pages.policeIncidentFormPage.formTitles.sectionOutcome")}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "primary.hint" }}>
          {subtitle}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "primary.hint" }}>
          {summary.posDisplayName}
        </Typography>
        {isAutoClosedForm && (
          <Chip
            label={t("pages.policeIncidentFormPage.outcomeForm.autoClosed")}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
      {!!menuOptions.length && (
        <Box
          ref={cardRef}
          onClick={() => {
            setActionsIsOpen(!actionsIsOpen);
          }}
          sx={{ cursor: "pointer", ml: 1 }}
        >
          <MenuFlyout
            cardRef={cardRef}
            options={menuOptions}
            isOpen={actionsIsOpen}
            onClose={() => {
              setActionsIsOpen(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// --- Main hook ---

/**
 * Hook that provides pre-rendered outcome form elements for each section
 * of the PoliceIncidentFormPage (in-progress, new forms, completed).
 *
 * Fetches outcome form summaries from the server and renders per-POS cards
 * with create / claim / continue actions.
 */
export function useOutcomeFormInProgressCards(
  formContext: FormContextData,
  isFinalised: boolean,
  shareableIncidentData: ShareableIncidentDataV1Response | null | undefined,
  subtitle: string,
  onViewPdf: (formId: string) => void,
  incidentId: string,
): {
  inProgressElements: ReactElement[];
  newFormElements: ReactElement[];
  completedElements: ReactElement[];
  completedFormIds: string[];
  isLoading: boolean;
} {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useContext(LoggedInUserContext);
  const currentUserId = user?.user.id;
  const currentTeamId = user?.user.sessionContext?.teamId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPosModal, setShowPosModal] = useState(false);

  const [{ data: outcomeFormSummaries, loading }, refetchOutcomeForms] =
    apiHooks.outcomeForm.getForFormContext(formContext.id);

  const incidentPlacesOfSafety = useMemo(
    () => shareableIncidentData?.placesOfSafety ?? [],
    [shareableIncidentData?.placesOfSafety],
  );

  // Derive POS from the current user's emha participant entry (set when
  // they accessed the incident via share code and selected their POS).
  // If the current user has no participant entry, fall back to the most
  // recent participant in the same team — this covers team members who
  // are assumed to be at the same POS as their teammate who confirmed it.
  const participantPos: OutcomeFormPlaceOfSafety | null = useMemo(() => {
    const participant = resolveEmhaParticipant(
      shareableIncidentData?.emhaParticipants ?? [],
      currentUserId,
      currentTeamId,
    );

    if (!participant?.placeOfSafety) return null;

    const pos = participant.placeOfSafety;
    if (pos.eMHACuratedPlaceOfSafetyId) {
      return { curatedPlaceOfSafetyId: pos.eMHACuratedPlaceOfSafetyId };
    }
    return {
      manualPosName: pos.name,
      manualPosAddress: pos.address,
      manualPosPostalCode: pos.postalCode,
      manualPosTrustId: pos.trustId ?? undefined,
    };
  }, [shareableIncidentData?.emhaParticipants, currentUserId, currentTeamId]);

  const summaries = Array.isArray(outcomeFormSummaries)
    ? outcomeFormSummaries
    : [];

  // Only include outcome forms that were actually completed (not those that
  // were force-finalised while still in-progress when the incident was closed).
  const completedSummaries = summaries.filter(
    (s) =>
      ["complete", "finalised"].includes(s.formStatus) &&
      ["completed", "auto-closed"].includes(s.completionStatus),
  );

  // Check if the current user's team has already completed an outcome form.
  // When true, block create/claim/continue for non-share-code users.
  const currentTeamHasCompletedForm = useMemo(
    () =>
      !!currentTeamId &&
      completedSummaries.some((s) => s.completedByTeamId === currentTeamId),
    [currentTeamId, completedSummaries],
  );

  const canCreateOutcomeForm =
    !isFinalised &&
    !!formContext.activeTeamworkWorkItem &&
    !currentTeamHasCompletedForm;

  const handleCreateOutcomeForm = async (
    placeOfSafety: OutcomeFormPlaceOfSafety,
  ) => {
    setIsSubmitting(true);
    try {
      const result = await api.outcomeForm.create({
        formContextId: formContext.id,
        placeOfSafety,
      });

      if (result.status === 200 || result.status === 201) {
        setShowPosModal(false);
        refetchOutcomeForms();
        navigate(
          routeFns.formDraftsComplete(
            result.data.formDraftId,
            formContext.patientId,
          ),
        );
      } else {
        renderErrorToast({
          message: t("pages.policeIncidentFormPage.outcomeForm.failedCreate"),
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        logErrorToDatadog(e);
      }
      renderErrorToast({
        message: t("pages.policeIncidentFormPage.outcomeForm.failedCreate"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClaimOutcomeForm = async (summary: OutcomeFormSummary) => {
    setIsSubmitting(true);
    try {
      const result = await api.outcomeForm.claim(summary.formId, {
        placeOfSafety: summary.placeOfSafety,
      });

      if (result.status === 200) {
        refetchOutcomeForms();

        const previousOwner = result.data.previousOwnerUserName;
        if (previousOwner) {
          renderSuccessToast({
            message: t("pages.policeIncidentFormPage.outcomeForm.claimedFrom", {
              name: previousOwner,
            }),
          });
        }

        navigate(
          routeFns.formDraftsComplete(
            result.data.formDraftId,
            formContext.patientId,
          ),
        );
      } else {
        renderErrorToast({
          message: t("pages.policeIncidentFormPage.outcomeForm.failedClaim"),
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        logErrorToDatadog(e);
      }
      renderErrorToast({
        message: t("pages.policeIncidentFormPage.outcomeForm.failedClaim"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inProgressSummaries = summaries.filter(
    (s) => s.formStatus === "in-progress",
  );

  // Build in-progress elements
  const inProgressElements = inProgressSummaries.map((summary) => {
    const isOwner = summary.currentOwnerUserId === currentUserId;
    const ownerLabel = summary.currentOwnerUserName ?? t("common.unknown");

    return (
      <OutcomeFormCard
        key={`outcome-in-progress-${summary.formId}`}
        summary={summary}
        isOwner={isOwner}
        ownerLabel={ownerLabel}
        patientId={formContext.patientId}
        subtitle={subtitle}
        onContinue={() => {
          if (summary.formDraftId) {
            navigate(
              routeFns.formDraftsComplete(
                summary.formDraftId,
                formContext.patientId,
              ),
            );
          }
        }}
        onClaim={() => void handleClaimOutcomeForm(summary)}
        isSubmitting={isSubmitting}
        isFinalised={isFinalised}
        isAtDifferentPos={
          currentTeamHasCompletedForm ||
          (!!participantPos &&
            !posMatches(summary.placeOfSafety, participantPos))
        }
      />
    );
  });

  // If the user has a POS from share-code access, check whether an
  // outcome form (in-progress or completed) already exists for that POS.
  const participantPosHasExistingForm = useMemo(() => {
    if (!participantPos) return false;
    return summaries.some((s) => posMatches(s.placeOfSafety, participantPos));
  }, [participantPos, summaries]);

  // Build new form elements — hidden when the user's POS already has an
  // in-progress form (they should continue/claim it instead).
  const newFormElements: ReactElement[] = [];
  if (canCreateOutcomeForm && !participantPosHasExistingForm) {
    newFormElements.push(
      <Fragment key="new-outcome-form-row">
        <FormCategoryRow
          buttonTestId="start-new-form-button--outcome"
          title={t("pages.policeIncidentFormPage.formTitles.sectionOutcome")}
          subtitle={subtitle}
          onLaunchForm={() => {
            // If the user already has a POS from share-code access, skip
            // the POS selection modal and create the form directly.
            if (participantPos) {
              void handleCreateOutcomeForm(participantPos);
            } else {
              setShowPosModal(true);
            }
          }}
          launchFormLabel={t(
            "pages.policeIncidentFormPage.outcomeForm.addOutcomeForm",
          )}
        />
        {!participantPos && (
          <OutcomeFormPosSelectionModal
            open={showPosModal}
            onClose={() => {
              setShowPosModal(false);
            }}
            onSelect={(pos) => void handleCreateOutcomeForm(pos)}
            incidentPlacesOfSafety={incidentPlacesOfSafety}
            isSubmitting={isSubmitting}
            existingOutcomeFormPosEntries={summaries.map(
              (s) => s.placeOfSafety,
            )}
          />
        )}
      </Fragment>,
    );
  }

  // Build completed elements
  const completedElements = completedSummaries.map((summary) => {
    const menuOptions = generateMenuOptions(
      summary.formId,
      onViewPdf,
      incidentId,
      "outcome-form",
      [],
    );

    return (
      <OutcomeFormCompletedCard
        key={`outcome-completed-${summary.formId}`}
        summary={summary}
        subtitle={subtitle}
        menuOptions={menuOptions}
      />
    );
  });

  return {
    inProgressElements,
    newFormElements,
    completedElements,
    completedFormIds: completedSummaries.map((s) => s.formId),
    isLoading: loading,
  };
}
